import { Card, CardActions, CardContent, Grid } from "@material-ui/core";
import { Close, Save } from "@material-ui/icons";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { getClickButton } from "../../functions/getButtons";
import { IAPIUpdates } from "../../models/IAPIUpdates";
import IIntegration from "../../models/IIntegration";
import { IMenuItem } from "../../models/IMenuItem";
import Services from "../../services/Services";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import { FormErrors } from "../forms/FormErrors";
import FormTextField from "../forms/FormTextField";
import { useIntegrationContext } from "./contexts/IntegrationContext";
import DataDirectionSelect from "./DataDirectionSelect";
import RemoveAPIsDialog from "./dialogs/apis/RemoveAPIsDialog";
import SystemSelect from "./SystemSelect";
import { IntegrationSystems } from "./IntegrationSystems";
import { IntegrationStatuses } from "./IntegrationStatuses";

interface IIntegrationDetailEditableSummaryCardProps extends RouteComponentProps {
  token: string;
  form: IIntegration;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOptionChange: (val: keyof IIntegration) => (val: IMenuItem | null) => void;
  handleSave: () => void;
  cancelEdit: () => void;
  formErrors: FormErrors<IIntegration>;
}

export const IntegrationDetailEditableSummaryCard = (props: IIntegrationDetailEditableSummaryCardProps) => {
  const { token, form, handleChange, handleOptionChange, handleSave, cancelEdit, formErrors } = props;
  const {
    showRemoveAPIsDialog,
    setShowRemoveAPIsDialog,
    setRemoveAPIsConfirmed,
    integrationCompanyId,
    integrationID,
    removeAPIsConfirmed,
    apis,
    setApis,
  } = useIntegrationContext();

  const confirmRemoveAPIs = () => {
    setRemoveAPIsConfirmed(true);
    setShowRemoveAPIsDialog(false);
  };

  const handleRemoveOnSave = async () => {
    if (apis && apis.length > 0 && removeAPIsConfirmed) {
      let integrationRepo = await new Services().IntegrationRepository();

      let newApis: IAPIUpdates = {
        AddedIntegrationMethodIDs: [],
        RemovedIntegrationMethodIDs: apis.map(a => a.ID),
      };

      let apiResp = await integrationRepo.updateAPIs(token, integrationCompanyId, integrationID, newApis);

      if (apiResp) {
        setApis([]);
      }
    }

    handleSave();
  };

  const isQmlativ = form.System.ID === IntegrationSystems.Qmlativ;
  const isNotStarted = form.Status.ID === IntegrationStatuses.NotStarted;

  return (
    <>
      <RemoveAPIsDialog isOpen={showRemoveAPIsDialog} cancel={() => setShowRemoveAPIsDialog(false)} submit={confirmRemoveAPIs} />
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField<IIntegration>
                name="Name"
                label="Integration Name"
                onChange={handleChange}
                form={form}
                formErrors={formErrors}
                required
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <SystemSelect id={form.System.ID} onValueChange={handleOptionChange("System")} required apis={apis} error={formErrors["System"]} />
            </Grid>
            <Grid item xs={12}>
              <DataDirectionSelect
                id={form.DataDirection.ID}
                onValueChange={handleOptionChange("DataDirection")}
                required
                error={formErrors["DataDirection"]}
              />
            </Grid>
            {isQmlativ && (
              <Grid item xs={12}>
                <FormTextField<IIntegration>
                  name="DevelopmentURL"
                  label="Development URL"
                  disabled={isNotStarted}
                  onChange={handleChange}
                  form={form}
                  formErrors={formErrors}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormTextField<IIntegration>
                name="Description"
                label="Description - what is the purpose of this Integration?"
                onChange={handleChange}
                form={form}
                formErrors={formErrors}
                required
                multiline
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField<IIntegration>
                name="PublishNotes"
                label="Notes"
                onChange={handleChange}
                form={form}
                formErrors={formErrors}
                multiline
                inputProps={{ maxLength: 1024 }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          {getClickButton("Save", <Save />, handleRemoveOnSave)}
          {getClickButton("Cancel", <Close />, cancelEdit)}
        </CardActions>
      </Card>
    </>
  );
};

export default connect((state: IApplicationState) => {
  return {
    ...selectAuth(state),
  };
})(withRouter(IntegrationDetailEditableSummaryCard));
